import { ADD_PROJECT_API_URL, DELETE_PROJECT_API_URL } from "../config/constants";
import { requestNotificationsRefresh } from "./notificationsApi";
import { apiFetch } from "../utils/apiClient";


const getAuthHeader = () => {
	const rawToken =
		sessionStorage.getItem("token") ||
		sessionStorage.getItem("authToken") ||
		sessionStorage.getItem("accessToken");
	const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

	if (!rawToken) {
		throw new Error("Authentication token not found. Please log in again.");
	}

	const normalizedToken = rawToken
		.replace(/^"|"$/g, "")
		.replace(/^Bearer\s+/i, "")
		.trim();

	return `${tokenType} ${normalizedToken}`;
};

const extractErrorMessage = async (response) => {
	try {
		const data = (await response.json());
		if (typeof data?.message === "string" && data.message.trim()) {
			return data.message;
		}
		if (typeof data?.error === "string" && data.error.trim()) {
			return data.error;
		}
	} catch {
		// Ignore parsing failure and use fallback message.
	}

	if (response.status === 401) {
		return "Unauthorized. Please log in again.";
	}

	if (response.status === 403) {
		return "You don't have permission to add projects.";
	}

	return "Failed to create project. Please try again.";
};

export async function addProject(payload) {
	const response = await apiFetch(ADD_PROJECT_API_URL, {
		method: "POST",
		headers: {
			Authorization: getAuthHeader(),
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		body: JSON.stringify(payload),
	}, "Failed to create project. Please try again.", { passthrough: [401, 403] });

	if (!response.ok) {
		throw new Error(await extractErrorMessage(response));
	}

	// PROJECT_CREATED — let notification surfaces refresh without a page reload.
	requestNotificationsRefresh();

	const contentType = response.headers.get("content-type") || "";
	return contentType.includes("application/json") ? await response.json() : null;
}


export async function deleteProject(projectId) {
	if (!projectId && projectId !== 0) {
		throw new Error("A valid project id is required to delete a project.");
	}

	const response = await apiFetch(DELETE_PROJECT_API_URL(projectId), {
		method: "DELETE",
		headers: {
			Authorization: getAuthHeader(),
			Accept: "application/json",
		},
	}, "Failed to delete project. Please try again.", { passthrough: [401, 403] });

	if (!response.ok) {
		let message = "Failed to delete project. Please try again.";

		try {
			const rawError = await response.text();

			if (rawError) {
				try {
					const data = JSON.parse(rawError);
					if (typeof data?.message === "string" && data.message.trim()) {
						message = data.message;
					} else if (typeof data?.error === "string" && data.error.trim()) {
						message = data.error;
					}
				} catch {
					// Response body is plain text, use it directly.
					message = rawError;
				}
			}
		} catch {
			// Ignore read failure and use fallback message.
		}

		if (response.status === 401) {
			message = "Unauthorized. Please log in again.";
		} else if (response.status === 403) {
			message = "You don't have permission to delete projects.";
		}

		throw new Error(message);
	}

	// PROJECT_DELETED — let notification surfaces refresh without a page reload.
	requestNotificationsRefresh();

	// The delete endpoint responds with a plain-text success message, so read the
	// body as text first and only parse it when it is actually valid JSON.
	const rawBody = await response.text();

	if (!rawBody) {
		return null;
	}

	try {
		return JSON.parse(rawBody);
	} catch {
		return { message: rawBody };
	}
}
